/**
 * Campaign tile matching the public-site opportunity card (3/4 cover,
 * lime badge, charcoal scrim, hover lift). A missing cover falls back to
 * an on-brand poster so the grid never shows a blank tile.
 */
import type { CSSProperties, MouseEvent, ReactNode } from "react";
import { formatMoney, fromMinorString, type CurrencyCode } from "@contracts/money";
import { PlatformRow } from "@app/shared/Platforms";

export function campaignRateLabel(minor: string, currency: string): string {
  return `${formatMoney(fromMinorString(minor, currency as CurrencyCode))} / 1K`;
}

export function campaignModeLabel(_mode?: string): string {
  return "Open to join";
}

export function campaignBadge(opts: {
  eligible?: boolean | null;
  joinClosesAt?: string | null;
  joinOpensAt?: string | null;
}): string {
  if (opts.joinOpensAt && new Date(opts.joinOpensAt).getTime() > Date.now()) return "Opens";
  if (!opts.joinClosesAt) return "Open";
  const days = Math.ceil((new Date(opts.joinClosesAt).getTime() - Date.now()) / 86_400_000);
  return days < 0 ? "Closed" : "Open";
}

export function campaignDeadline(iso: string | null | undefined): string {
  if (!iso) return "no deadline set";
  const days = Math.ceil((new Date(iso).getTime() - Date.now()) / 86_400_000);
  if (days < 0) return "closed";
  if (days === 0) return "closes today";
  return days === 1 ? "1 day left" : `${days} days left`;
}

const cover: CSSProperties = {
  position: "absolute",
  inset: 0,
  background: "linear-gradient(160deg, var(--surface-3) 0%, var(--chrome-bg) 58%, #0C0D0B 100%)",
};

/** On-brand poster used when a campaign has no cover asset. */
export function CampaignCover({ brand, name }: { brand: string; name: string }) {
  const initial = (name.trim()[0] || brand.trim()[0] || "C").toUpperCase();
  return (
    <div data-tile-media="" aria-hidden="true" style={cover}>
      <span style={{
        position: "absolute", right: -6, top: 28,
        fontSize: 148, fontWeight: 800, letterSpacing: "-0.07em",
        color: "var(--accent)", opacity: 0.12, lineHeight: 1,
      }}>
        {initial}
      </span>
      <span style={{
        position: "absolute", left: 22, top: 64, width: 64, height: 7,
        borderRadius: 99, background: "var(--accent)", opacity: 0.45,
      }} />
      <span style={{
        position: "absolute", left: 22, top: 80, width: 104, height: 7,
        borderRadius: 99, background: "var(--accent)", opacity: 0.22,
      }} />
      <span style={{
        position: "absolute", left: 22, top: 96, width: 40, height: 7,
        borderRadius: 99, background: "var(--accent)", opacity: 0.14,
      }} />
    </div>
  );
}

export function CampaignTile({
  brand, name, rateLabel, badge, meta, platforms, href, onOpen, img, rateDetail,
}: {
  brand: string;
  name: string;
  rateLabel: string;
  badge: string;
  meta?: string;
  platforms?: readonly string[];
  href: string;
  onOpen: () => void;
  img?: string | undefined;
  /** Per-platform prices. When set, this replaces the single rate line. */
  rateDetail?: ReactNode | undefined;
}) {
  function open(e: MouseEvent) {
    e.preventDefault();
    onOpen();
  }

  return (
    <a
      href={href}
      data-tile=""
      onClick={open}
      style={{
        position: "relative", display: "block", aspectRatio: "3 / 4", borderRadius: 20,
        overflow: "hidden", textDecoration: "none", color: "#FFFFFF",
        background: "var(--surface)", border: "1px solid var(--line)",
      }}
    >
      {img ? (
        <img data-tile-media="" src={img} alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover" }} />
      ) : (
        <CampaignCover brand={brand} name={name} />
      )}
      <span aria-hidden="true" style={{
        position: "absolute", left: 0, right: 0, bottom: 0, height: "46%",
        pointerEvents: "none",
        backdropFilter: "blur(18px)",
        WebkitBackdropFilter: "blur(18px)",
        maskImage: "linear-gradient(to top, #000 0%, #000 42%, transparent 100%)",
        WebkitMaskImage: "linear-gradient(to top, #000 0%, #000 42%, transparent 100%)",
      }} />
      <span aria-hidden="true" style={{
        position: "absolute", left: 0, right: 0, bottom: 0, height: "46%",
        pointerEvents: "none",
        background: "linear-gradient(to top, rgba(6,7,8,.82) 0%, rgba(6,7,8,.28) 62%, rgba(6,7,8,0) 100%)",
      }} />
      <span style={{
        position: "absolute", top: 14, left: 14, padding: "4px 10px", borderRadius: 999,
        fontSize: 11, fontWeight: 700, letterSpacing: ".04em",
        background: "var(--tone-accent-bg)", color: "var(--tone-accent-fg)",
      }}>
        {badge}
      </span>
      <span data-tile-arrow="" aria-hidden="true" style={{
        position: "absolute", top: 14, right: 14, width: 30, height: 30,
        display: "grid", placeItems: "center", borderRadius: "50%",
        background: "var(--accent)", color: "var(--accent-fg)", fontWeight: 700,
      }}>
        →
      </span>
      <span style={{
        position: "absolute", left: 0, right: 0, bottom: 0,
        padding: "16px 18px 16px", display: "grid", gap: 3,
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: "rgba(255,255,255,.86)" }}>{brand}</span>
        <span style={{ fontSize: 18, fontWeight: 700, letterSpacing: "-0.01em" }}>{name}</span>
        {rateDetail ?? (
          <span style={{
            marginTop: 4, fontFamily: "var(--font-mono)", fontSize: 13,
            color: "var(--hero-accent)", fontVariantNumeric: "tabular-nums",
          }}>
            {rateLabel} views
          </span>
        )}
        {!rateDetail && platforms && platforms.length > 0 ? (
          <span style={{ marginTop: 6 }}>
            <PlatformRow routes={platforms} size={20} />
          </span>
        ) : null}
        {meta ? (
          <span style={{ fontSize: 11, color: "rgba(255,255,255,.62)" }}>{meta}</span>
        ) : null}
      </span>
    </a>
  );
}
